const pageGroup = require('./pageGroup')
const { UTMS, CAPS } = require('./attribution')

// Plausible "Trial CTA Click" for every link to pricing or signup, from listeners on document. The same links forward
// the page's UTMs and affiliate ref (no storage, works without consent; docs/marketing/app-handoff-contract.md).
// Details: docs/marketing/tracking-contract.md. Identical in www/ and blog/ (www/test/trial-cta.test.js checks).
const EVENT = 'Trial CTA Click'
const HOLD_MS = 600

const destinationOf = (href, base) => {
  let url
  try {
    url = new URL(href, base)
  } catch {
    return null
  }
  if (!/^https?:$/.test(url.protocol)) return null
  const host = url.hostname.toLowerCase()
  if (host === 'get.myphoner.com' && url.pathname.startsWith('/signup')) return 'signup'
  const onSite = host === new URL(base).hostname || host === 'www.myphoner.com'
  if (onSite && /^\/([a-z]{2}\/)?pricing\/?$/.test(url.pathname)) return 'pricing'
  return null
}

// The page's UTMs and affiliate ref, added to a trial CTA link so signups keep their campaign without a cookie.
// UTMs only when the link has none of its own, ref only when it has no ref. Returns null when nothing changes.
const forwardedHref = (href, pageUrl) => {
  if (!destinationOf(href, pageUrl)) return null
  const page = new URL(pageUrl).searchParams
  const url = new URL(href, pageUrl)
  const own = [...url.searchParams.keys()]
  const forward = own.some((k) => k.startsWith('utm_')) ? [] : [...UTMS]
  if (!own.includes('ref')) forward.push('ref')
  let changed = false
  for (const key of forward) {
    const value = page.get(key)
    if (!value) continue
    url.searchParams.append(key, Array.from(value).slice(0, CAPS.utm).join(''))
    changed = true
  }
  if (!changed) return null
  return /^https?:\/\//i.test(href) ? url.href : `${url.pathname}${url.search}${url.hash}`
}

const ctaLocation = (link) => {
  const marked = link.closest('[data-cta-location]')
  if (marked) return marked.getAttribute('data-cta-location')
  if (link.closest('header')) return 'header'
  if (link.closest('footer')) return 'footer'
  return 'inline'
}

const send = (win, options) => {
  try {
    win.plausible(EVENT, options)
  } catch {
    // analytics must never break a link
  }
}

const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
const isSameTab = (link) => !link.target || link.target === '_self'

// Capture phase: runs before React and the tracking listener, so every path (new tab, held signup click, Gatsby Link) sees the tagged URL
const forwardOnClick = (win, navigate) => (event) => {
  const link = event.target && event.target.closest ? event.target.closest('a[href]') : null
  if (!link) return
  const tagged = forwardedHref(link.getAttribute('href'), win.location.href)
  if (!tagged) return
  link.setAttribute('href', tagged)
  // Gatsby's Link navigates from its `to` prop, not the href, and skips clicks that are already default-prevented
  const internal = !/^https?:\/\//i.test(tagged)
  if (internal && event.type === 'click' && isPlainClick(event) && isSameTab(link) && !event.defaultPrevented) {
    event.preventDefault()
    if (navigate) navigate(tagged)
    else win.location.assign(tagged)
  }
}

const initTrialCtaTracking = (win, { navigate } = {}) => {
  const forward = forwardOnClick(win, navigate)
  win.document.addEventListener('click', forward, true)
  win.document.addEventListener('auxclick', forward, true)
  win.document.addEventListener('click', (event) => {
    const link = event.target && event.target.closest ? event.target.closest('a[href]') : null
    if (!link || typeof win.plausible !== 'function') return
    const destination = destinationOf(link.getAttribute('href'), win.location.href)
    if (!destination) return
    const props = { destination, cta_location: ctaLocation(link), page_group: pageGroup(win.location.pathname) }

    // Absolute links load a new page (signup, blog to www), which can cancel the request: wait for it, briefly
    const fullLoad = /^https?:\/\//i.test(link.getAttribute('href'))
    const plainClick = isPlainClick(event)
    const sameTab = isSameTab(link)
    // Only the real script answers (it sets plausible.l); with just the queue stub (blocked), holding only adds delay
    const scriptLoaded = win.plausible.l === true
    if (fullLoad && plainClick && sameTab && scriptLoaded && !event.defaultPrevented) {
      event.preventDefault()
      let gone = false
      const go = () => {
        if (gone) return
        gone = true
        win.location.assign(link.href)
      }
      // The fallback timer comes first: navigation must not depend on Plausible working
      win.setTimeout(go, HOLD_MS)
      send(win, { props, callback: go })
    } else {
      send(win, { props })
    }
  })
}

module.exports = { destinationOf, forwardedHref, ctaLocation, initTrialCtaTracking, HOLD_MS }
